import React, { FC, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import userMobx from '../../../../../../mobx/user'
import { BrandI, Guest } from '../../../../../../types-project/Client'

interface GuestsProps {
  brandId: string
}

const VOGZ_GRADIENT = 'linear-gradient(45deg, #615AF6, #2A42A7)'

const isValidEmail = (value: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim())

const Guests: FC<GuestsProps> = ({ brandId }) => {
  const { t } = useTranslation()
  const [emails, setEmails] = useState<string[]>([''])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (brandId) userMobx.getListGuestsFromBrand(brandId)
  }, [brandId])

  const guests = ((userMobx.brand.data as BrandI<Guest[]> | null)?.guests ?? []) as Guest[]

  const updateEmail = (index: number, value: string) => {
    setEmails((prev) => {
      const copy = [...prev]
      copy[index] = value
      return copy
    })
  }

  const addInput = () => {
    setEmails((prev) => (prev.length < 6 ? [...prev, ''] : prev))
  }

  const invite = async () => {
    const toInvite = emails.map((e) => e.trim()).filter((e) => e !== '')
    if (toInvite.length === 0) return
    if (toInvite.some((e) => !isValidEmail(e))) {
      toast.error(t('myprofil:brand-page.guests.invalid-email'), { theme: 'colored' })
      return
    }
    setSubmitting(true)
    await userMobx.updateBrand(brandId, { guests: toInvite } as BrandI<string[]>, async () => {
      await userMobx.getListGuestsFromBrand(brandId)
      setEmails([''])
      toast.success(t('myprofil:brand-page.guests.invite'), { theme: 'colored' })
    })
    setSubmitting(false)
  }

  const remove = (idGuest: string) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`${t('myprofil:brand-page.guests.remove')} ?`)) {
      userMobx.deleteBrandGuest(brandId, idGuest)
    }
  }

  return (
    <div className="w-full" style={{ maxWidth: 560, margin: '0 auto' }}>
      <h3 style={{ color: '#2A42A7', fontFamily: 'Hurme-Black, sans-serif', fontSize: 18 }}>
        {t('myprofil:brand-page.guests.title')}
      </h3>
      <p style={{ color: '#9aa0bd', fontSize: 14, marginTop: 4, marginBottom: 20 }}>
        {t('myprofil:brand-page.guests.desc')}
      </p>

      <div className="flex flex-col gap-2">
        {emails.map((email, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="flex items-center gap-2">
            <input
              type="email"
              value={email}
              placeholder={t('myprofil:brand-page.guests.email-placeholder')}
              onChange={(e) => updateEmail(index, e.target.value)}
              className="flex-1"
              style={{
                borderRadius: 10,
                border: '1px solid #E2E5F0',
                padding: '10px 12px',
                fontSize: 14,
                outline: 'none',
                background: '#fff',
              }}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          onClick={addInput}
          disabled={emails.length >= 6}
          className="flex items-center gap-1.5"
          style={{
            color: '#615AF6',
            fontFamily: 'Hurme-Bold, sans-serif',
            fontSize: 14,
            cursor: emails.length >= 6 ? 'default' : 'pointer',
            opacity: emails.length >= 6 ? 0.4 : 1,
          }}
        >
          <i className="fa-light fa-circle-plus" /> {t('myprofil:brand-page.guests.add-input')}
        </button>
        <button
          type="button"
          onClick={invite}
          disabled={submitting}
          className="ml-auto"
          style={{
            borderRadius: 10,
            background: VOGZ_GRADIENT,
            color: '#fff',
            fontFamily: 'Hurme-Bold, sans-serif',
            fontSize: 14,
            padding: '10px 20px',
            boxShadow: '0 4px 14px rgba(97,90,246,.3)',
            cursor: submitting ? 'default' : 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}
        >
          {t('myprofil:brand-page.guests.invite')}
        </button>
      </div>

      <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #EEF0F7' }} />

      {guests.length === 0 ? (
        <p style={{ color: '#9aa0bd', fontSize: 14, fontStyle: 'italic' }}>
          {t('myprofil:brand-page.guests.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {guests.map((guest) => (
            <li
              key={guest._id}
              className="flex items-center justify-between"
              style={{ borderRadius: 10, background: '#F5F6FB', padding: '10px 14px' }}
            >
              <span style={{ color: '#444', fontSize: 14 }}>{guest.email}</span>
              <button
                type="button"
                onClick={() => remove(guest._id)}
                style={{ color: '#ef4444', fontFamily: 'Hurme-Bold, sans-serif', fontSize: 12, cursor: 'pointer' }}
              >
                {t('myprofil:brand-page.guests.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default observer(Guests)
